import state from '@/state';
import advancedString from '@/utils/advancedString';

const t = state.t.prefix('notifications');

function tFallback (...phrases) {
    // Prefer the current language
    for (const phrase of phrases) {
        const value = t(phrase, true);
        if (value) {
            return value;
        }
    }

    // With fallback to english
    for (const phrase of phrases) {
        const value = t(phrase);
        if (value) {
            return value;
        }
    }

    return null;
}

function getNamesFragment (names) {
    // @TODO ensure unique nameList

    if (names.length > 2) {
        return advancedString(t('fragments.names.many'), {
            name1: names[0],
            name2: names[1],
            remainderCount: names.length - 2,
        });
    } else if (names.length == 2) {
        return advancedString(t('fragments.names.two'), {
            name1: names[0],
            name2: names[1],
        });
    } else if (names.length == 1) {
        return advancedString(t('fragments.names.one'), {
            name1: names[0],
        });
    }
    return t('fragments.names.unknown');
}

function capitalize (str) {
    if (! str) {
        return;
    }
    return str.slice(0, 1).toUpperCase() + str.slice(1);
}

function getYourCommentOnFragment (contextData) {
    const phrases = ['fragments.your_comment_on.' + contextData.commentType, 'fragments.your_comment_on.generic'];

    if (contextData.isReply) {
        phrases.unshift('fragments.your_reply_comment_on.' + contextData.commentType);
    }

    return tFallback(...phrases);
}

function getActionPhrase (comment) {
    const targetName = comment.target && comment.target.name || null;
    
    let str = '**' + comment.author.firstName + '** ';

    if (! targetName) {
        str += state.t('comments.action_phrase_product.' + comment.type.name) || state.t('comments.action_phrase_product.product_comment');
    }
    
    str += state.t('comments.action_phrase.' + comment.type.name) || state.t('comments.action_phrase.product_comment');
    str += ' **' + targetName + '**';

    return str;
}

const notificationTranslators = {
    achievement_unlocked (contextData) {
        let achievementSuffix = '';
        const suffixType = state.merchantConfig.achievements?.listSuffix;
        if (suffixType === 'level' && contextData.achievementMaxLevel > 1) {
            achievementSuffix = ' - Lv. ' + contextData.achievementLevel;
        }
        if (suffixType === 'tierName' && contextData.achievementTierName) {
            achievementSuffix = ' - ' + contextData.achievementTierName;
        }
        return advancedString(t('achievement_unlocked'), {
            achievementName: contextData.achievementName,
            achievementSuffix,
        });
    },
    question_answered (contextData) {
        return advancedString(t('comment_answered'), {
            names: getNamesFragment(contextData.names),
            yourCommentOn: tFallback('fragments.your_comment_on.' + contextData.commentType, 'fragments.your_comment_on.generic'),
            targetName: contextData.targetName || '',
        });
    },
    comment_answered (contextData) {
        return advancedString(t('comment_answered'), {
            names: getNamesFragment(contextData.names),
            yourCommentOn: tFallback('fragments.your_comment_on.' + contextData.commentType, 'fragments.your_comment_on.generic'),
            targetName: contextData.targetName || '',
        });
    },
    followed_comment_answered (contextData) {
        return advancedString(t('comment_answered'), {
            names: getNamesFragment(contextData.names),
            yourCommentOn: tFallback('fragments.followed_comment_on.' + contextData.commentType, 'fragments.followed_comment_on.generic'),
            targetName: contextData.targetName || '',
        });
    },
    followed_customer_posted (contextData) {
        let txt = getActionPhrase(contextData.comment);
        if (contextData.comment?.shortText) {
            txt += ': *' + contextData.comment.shortText + '*';
        }
        return txt;
    },
    followed_board_posted (contextData) {
        let txt = getActionPhrase(contextData.comment);
        if (contextData.comment?.shortText) {
            txt += ': *' + contextData.comment.shortText + '*';
        }
        return txt;
    },
    answer_question_request (contextData) {
        return advancedString(t('answer_question_request'), {
            names: getNamesFragment(contextData.names),
            targetName: contextData.targetName || '',
        });
    },
    elected_comment_highlight (contextData) {
        return advancedString(t('elected_comment_highlight'), {
            targetName: contextData.targetName || '',
        });
    },
    comment_approved (contextData) {
        return advancedString(t('comment_approved'), {
            yourCommentOn: capitalize(getYourCommentOnFragment(contextData)),
            targetName: contextData.targetName || '',
        });
    },
    comment_upvoted (contextData) {
        return advancedString(t('comment_upvoted'), {
            names: getNamesFragment(contextData.names),
            yourCommentOn: getYourCommentOnFragment(contextData),
            targetName: contextData.targetName || '',
        });
    },
};

export default function getNotificationText (ev) {
    if (! ev.contextDataJson) {
        console.error('Notification event missing context data', ev);
        return null;
    }
    const translator = notificationTranslators[ev.type.name];
    if (! translator) {
        console.error('Missing translator for notification event type', ev.type.name);
        return null;
    }

    const contextData = JSON.parse(ev.contextDataJson);
    const text = translator(contextData);
    if (! text) {
        return null;
    }
    return text.trim().replace(/\s+/g, ' ');
}